import React from "react";
import { SidebarNav } from "./SidebarNav";
import { SidebarShareCard } from "./SidebarShareCard";
import { useReferrals } from "@/providers/ReferralsProvider";
import type { DesktopLayoutProps } from "./DesktopLayout.types";

/**
 * Desktop-specific layout with three-column design:
 * - Left: Navigation sidebar with logo and links
 * - Center: Main content area
 * - Right: Additional information sidebar (optional)
 */
export const DesktopLayout: React.FC<DesktopLayoutProps> = ({
  children,
  pageTitle,
  pageDescription,
  rightSidebar,
  showRightSidebar = !!rightSidebar,
  contentClassName = "",
  headerActions,
}) => {
  // Get referral link from context if available
  const { data } = useReferrals();
  const referralLink = data?.referralStats?.referralLink || null;

  return (
    <div className="min-h-screen bg-primary flex">
      {/* Left Sidebar - Navigation */}
      <SidebarNav
        bottomContent={<SidebarShareCard referralLink={referralLink} />}
      />

      {/* Main Content Area */}
      <div className="flex-1 flex">
        <main className={`flex-1 h-screen overflow-y-auto ${contentClassName}`}>
          {/* Page Header */}
          <div className="border-b border-border bg-primary backdrop-blur-sm sticky top-0 z-10">
            <div className="px-8 py-6 flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-text-primary">
                  {pageTitle}
                </h1>
                {pageDescription && (
                  <p className="text-text-secondary mt-1">{pageDescription}</p>
                )}
              </div>
              {headerActions && (
                <div className="flex items-center gap-3">{headerActions}</div>
              )}
            </div>
          </div>

          {/* Page Content */}
          <div className="p-8">{children}</div>
        </main>

        {/* Right Sidebar - Additional Info */}
        {showRightSidebar && rightSidebar && (
          <aside className="w-auto bg-secondary border-l border-border h-screen sticky top-0 overflow-y-auto">
            <div className="p-6">{rightSidebar}</div>
          </aside>
        )}
      </div>
    </div>
  );
};
